import { Header } from "build-ts/metadata";
import React, { useRef, useState } from "react";
import {
  Button,
  Text,
  Menu,
  MenuItem,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Icon,
  HStack,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { BiNetworkChart } from "react-icons/bi";
import { FaChartPie, FaCheckCircle } from "react-icons/fa";
import CustomModal from "../modals/CustomModal";
import EditMetricDefinitionContainer from "containers/configuration/EditMetricDefinitionContainer";
import { ColumnType, columnTypeToJSON, MetadataTypeEnum } from "build-ts/enums";
import { useConfigurationContext } from "context/ConfigurationContext";
import MKGVisualizerContainer from "containers/configuration/mkg/MKGVisualizerContainer";
import EditKGColumnComponent from "components/configuration/mkg/EditKGColumnComponent";
import EditBenchmarkComponent from "components/configuration/mkg/EditBenchmarkComponent";
import EditMKGContainer from "containers/configuration/mkg/EditMKGContainer";
import DependenciesModal from "./DependenciesModal";
import { MdBarChart, MdEdit, MdNumbers } from "react-icons/md";
import { PiTarget } from "react-icons/pi";
import { FiCalendar } from "react-icons/fi";

interface ViewMenu {
  isClicked: boolean;
  selectedNode: Header;
  nodeType: MetadataTypeEnum;
  isOpen: boolean;
  onClose: () => void;

  // Metric
  editMetricDefinition: boolean;
  editMKG: boolean;
  openViewMKG: boolean;
  handleEditMetricDefinitionModal: (metricHeader: Header) => void;
  handleEditMKGModal: (metricHeader: Header, modelId: string) => void;
  handleViewMKGModal: (metricHeader: Header) => void;
  handleDeleteMetricDialog: (id: string) => void;
  handleDeleteMetric: (id: string) => void;
  closeDeleteMetricDialog: () => void;
  closeDeleteMetric: () => void;
  openDeleteMetric: boolean;
  openDeleteMetricDialog: boolean;

  // Benchmark
  editBenchmark: boolean;
  handleEditBenchmarkModal: (benchmarkHeader: Header) => void;
  handleDeleteBenchmarkDialog: (id: string) => void;
  handleDeleteBenchmark: (id: string) => void;
  closeDeleteBenchmarkDialog: () => void;
  closeDeleteBenchmark: () => void;
  openDeleteBenchmark: boolean;
  openDeleteBenchmarkDialog: boolean;

  // KG Column
  editKgColumn: boolean;
  handleEditKgColumnModal: (kgColumnHeader: Header) => void;
  handleDeleteKgColumnDialog: (id: string) => void;
  handleDeleteKgColumn: (id: string) => void;
  closeDeleteKgColumnDialog: () => void;
  closeDeleteKgColumn: () => void;
  openDeleteKgColumn: boolean;
  openDeleteKgColumnDialog: boolean;
};

const MKGNodeMenuComponent: React.FC<ViewMenu> = ({
  isClicked,
  selectedNode,
  nodeType,
  isOpen,
  onClose,

  // Metric
  editMetricDefinition,
  editMKG,
  openViewMKG,
  handleEditMetricDefinitionModal,
  handleEditMKGModal,
  handleViewMKGModal,
  handleDeleteMetricDialog,
  handleDeleteMetric,
  closeDeleteMetricDialog,
  closeDeleteMetric,
  openDeleteMetric,
  openDeleteMetricDialog,

  // Benchmark
  editBenchmark,
  handleEditBenchmarkModal,
  handleDeleteBenchmarkDialog,
  handleDeleteBenchmark,
  closeDeleteBenchmarkDialog,
  closeDeleteBenchmark,
  openDeleteBenchmark,
  openDeleteBenchmarkDialog,

  // KG Column
  editKgColumn,
  handleEditKgColumnModal,
  handleDeleteKgColumnDialog,
  handleDeleteKgColumn,
  closeDeleteKgColumnDialog,
  closeDeleteKgColumn,
  openDeleteKgColumn,
  openDeleteKgColumnDialog,
}) => {
  const {
    metricInfo,
    selectedMetric,
    selectedBenchmarkId,
    dependencies,
  } = useConfigurationContext();

  const cancelDeleteRef = useRef<HTMLButtonElement>(null);
  const [triggerSubmit, setTriggerSubmit] = useState<boolean>(false);

  // Function to trigger submit (for metric definition and MKG)
  const handleSave = () => {
    setTriggerSubmit(true);
  };

  // Create save button for modal header
  const saveButton = (
    <Button
      colorScheme="green"
      size="sm"
      onClick={handleSave}
      leftIcon={<FaCheckCircle />}
      fontWeight="bold"
    >
      Save
    </Button>
  );
  
  return (
    <>
    {isClicked && nodeType === MetadataTypeEnum.METRIC && (
        <div style={{
          position: "fixed",
          top: "90.5%",
          left: "92.5%",
          transform: "translate(-50%, -50%)",
          zIndex: 1000,
          backgroundColor: "white",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          padding: "8px 0",
          minWidth: "250px",
          maxWidth: "250px",
        }}>
          <HStack justify="start" align="center" mx={1} p={0} mb={1}>
            <Icon as={MdBarChart} boxSize={4} color="gray.500"/>
            <Text 
              fontSize="xs" 
              fontWeight="bold" 
              color="gray.500" 
              letterSpacing="wider"
              textTransform="uppercase"
              mb={0}
            >
              {selectedNode?.name} (Metric)
            </Text>
          </HStack>
          <Menu>
            <MenuItem
              fontSize="sm"
              fontWeight={600}
              icon={<Icon as={MdEdit} color="gray.700" boxSize={4}/>}
              onClick={() => handleEditMetricDefinitionModal(selectedNode!)}
              style={{ padding: "8px 16px", cursor: "pointer" }}
            >
              Definition
            </MenuItem>
            <MenuItem
              fontSize="sm"
              fontWeight={600}
              icon={<Icon as={BiNetworkChart} color="gray.700" boxSize={4}/>}
              onClick={() => handleEditMKGModal(selectedNode!, selectedNode?.owner_id!)}  
              style={{ padding: "8px 16px", cursor: "pointer" }}
            >
              Edges
            </MenuItem>
            <MenuItem
              fontSize="sm"
              fontWeight={600}
              icon={<Icon as={DeleteIcon} color="red.500" boxSize={4}/>}
              onClick={() => handleDeleteMetricDialog(selectedNode?.id!)}
              style={{ padding: "8px 16px", cursor: "pointer" }}
            >
              <Text mb={0} color="red">Delete</Text>
            </MenuItem>
          </Menu>
        </div>
    )}
    {isClicked && nodeType === MetadataTypeEnum.BENCHMARK && (
        <div style={{
          position: "fixed",
          top: "90.5%",
          left: "92.5%",
          transform: "translate(-50%, -50%)",
          zIndex: 1000,
          backgroundColor: "white",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          padding: "8px 0",
          minWidth: "250px",
        }}>
          <HStack justify="start" align="center" mx={1} p={0} mb={1}>
            <Icon as={PiTarget} boxSize={4} color="gray.500"/>
            <Text 
              fontSize="xs" 
              fontWeight="bold" 
              color="gray.500" 
              letterSpacing="wider"
              textTransform="uppercase"
              mb={0}
            >
              {selectedNode?.name} (Benchmark)
            </Text>
          </HStack>
          <Menu>
            <MenuItem
              fontSize="sm"
              fontWeight={600}
              icon={<Icon as={MdEdit} color="gray.700" boxSize={4}/>}
              onClick={() => handleEditBenchmarkModal(selectedNode!)}
              style={{ padding: "8px 16px", cursor: "pointer" }}
            >
              Definition
            </MenuItem>
            <MenuItem
              fontSize="sm"
              fontWeight={600}
              icon={<Icon as={DeleteIcon} color="red.500" boxSize={4}/>}
              onClick={() => handleDeleteBenchmarkDialog(selectedNode?.id!)}
              style={{ padding: "8px 16px", cursor: "pointer" }}
            >
              <Text mb={0} color="red">Delete</Text>
            </MenuItem>
          </Menu>
        </div>
    )}
    {isClicked && nodeType === MetadataTypeEnum.KG_COLUMN && (
        <div style={{
          position: "fixed",
          top: "90.5%",
          left: "92.5%",
          transform: "translate(-50%, -50%)",
          zIndex: 1000,
          backgroundColor: "white",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          padding: "8px 0",
          minWidth: "250px",
        }}>
          <HStack justify="start" align="center" mx={1} p={0} mb={1}>
          {selectedNode?.column_type === ColumnType.MEASURE ? <Icon as={MdNumbers} boxSize={4} color="gray.500"/> 
            : selectedNode?.column_type === ColumnType.ATTRIBUTE ? <Icon as={FaChartPie} boxSize={4} color="gray.500"/>
            : <Icon as={FiCalendar} boxSize={4} color="gray.500"/>}
            <Text 
              fontSize="xs" 
              fontWeight="bold" 
              color="gray.500" 
              letterSpacing="wider"
              textTransform="uppercase"
              mb={0}
            >
              {selectedNode?.name} ({columnTypeToJSON(selectedNode?.column_type as ColumnType)})
            </Text>
          </HStack>
          <Menu>
            <MenuItem
              fontSize="sm"
              fontWeight={600}
              icon={<Icon as={MdEdit} color="gray.700" boxSize={4}/>}
              onClick={() => handleEditKgColumnModal(selectedNode!)}
              style={{ padding: "8px 16px", cursor: "pointer" }}
            >
              Definition
            </MenuItem>
            <MenuItem
              fontSize="sm"
              fontWeight={600}
              icon={<Icon as={DeleteIcon} color="red.500" boxSize={4}/>}
              onClick={() => handleDeleteKgColumnDialog(selectedNode?.id!)}
              style={{ padding: "8px 16px", cursor: "pointer" }}
            >
              <Text mb={0} color="red">Delete</Text>
            </MenuItem>
          </Menu>
        </div>
    )}
    
    {/* Metric Modals */}
    {editMKG && (
        <CustomModal
            isOpen={isOpen}
            onClose={onClose}
            title={`Edit Knowledge Graph for: ${selectedMetric?.name}`}
            maxWidth="90vw"
            maxHeight="90vh"
            headerAction={saveButton}
        >
            <EditMKGContainer
              metricInfo={metricInfo}
              triggerSubmit={triggerSubmit}
              setTriggerSubmit={setTriggerSubmit}
            />
        </CustomModal>
    )}
    
    {editMetricDefinition && (
        <CustomModal
            isOpen={isOpen}
            onClose={onClose}
            title={
              <>
                Edit Metric Definition for: <Text fontWeight="bold" display="inline">{selectedNode?.name}</Text>
              </>
            }
            maxWidth="90vw"
            maxHeight="90vh"
            headerAction={saveButton}
        >
            <EditMetricDefinitionContainer
              createMetricFlag={false}
              modelInfo={null}
              metricHeader={selectedMetric}
              onClose={onClose}
              triggerSubmit={triggerSubmit}
              setTriggerSubmit={setTriggerSubmit}
            />
        </CustomModal>
    )}

    {openViewMKG && (
      <CustomModal
        isOpen={isOpen}
        onClose={onClose}
        title={`Knowledge Graph for: ${selectedMetric?.name}`}
        maxWidth="90vw"
        maxHeight="90vh"
      >
        <MKGVisualizerContainer metricId={selectedMetric!} />
      </CustomModal>
    )}
    
    {/* Metric Delete Modals */}
    {openDeleteMetricDialog && (
      <AlertDialog
        isOpen={openDeleteMetricDialog}
        leastDestructiveRef={cancelDeleteRef}
        onClose={closeDeleteMetricDialog}
      >
        <AlertDialogOverlay
            style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}
        >
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete Metric
            </AlertDialogHeader>

            <AlertDialogBody>
                Are you sure you want to delete this metric?
            </AlertDialogBody>

            <AlertDialogFooter>
                <Button
                ref={cancelDeleteRef}
                colorScheme="green"
                onClick={closeDeleteMetricDialog}
                >
                Cancel
                </Button>
                <Button
                colorScheme="red"
                onClick={() => handleDeleteMetric(selectedNode?.id!)}
                ml={3}
                >
                Delete
                </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
          )}
    {/* Metric Dependencies Modal */}
    {dependencies.length > 0 && (
      <DependenciesModal
        isOpen={openDeleteMetric}
        onClose={closeDeleteMetric}
        dependencies={dependencies}
        onViewMKG={handleViewMKGModal}
      />
    )}

    {/* Benchmark Modals */}
    {editBenchmark && (
      <CustomModal
        isOpen={isOpen}
        onClose={onClose}
        title={`Edit Benchmark - ${selectedBenchmarkId?.header?.name}`}
        size="6xl"
        headerAction={saveButton}
      >
        <EditBenchmarkComponent
          benchmark={selectedBenchmarkId!}
          onClose={onClose}

          // For saving the Benchmark
          triggerSubmit={triggerSubmit}
          setTriggerSubmit={setTriggerSubmit}
        />
      </CustomModal>
    )}

    {/* Benchmark Delete Modals */}
    {openDeleteBenchmarkDialog && (
      <AlertDialog 
        isOpen={openDeleteBenchmarkDialog}
        leastDestructiveRef={cancelDeleteRef}
        onClose={closeDeleteBenchmarkDialog}
      >
        <AlertDialogOverlay
          style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}
        >
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete Benchmark
            </AlertDialogHeader>

            <AlertDialogBody>
                Are you sure you want to delete this benchmark?
            </AlertDialogBody>

            <AlertDialogFooter>
                <Button
                ref={cancelDeleteRef}
                colorScheme="green"
                onClick={closeDeleteBenchmarkDialog}
                >
                Cancel
                </Button>
                <Button
                colorScheme="red"
                onClick={() => handleDeleteBenchmark(selectedNode?.id!)}
                ml={3}
                >
                Delete
                </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
                </AlertDialogOverlay>
      </AlertDialog>
    )}
    {/* Benchmark Dependencies Modal */}
    {dependencies.length > 0 && (
    <DependenciesModal
      isOpen={openDeleteBenchmark}
        onClose={closeDeleteBenchmark}
        dependencies={dependencies}
      />
    )}

    {/* KG Column Modals */}
    {editKgColumn && (
      <CustomModal
        isOpen={isOpen}
        onClose={onClose}
        title="Edit KG Column Configuration"
        size="xl"
        maxWidth="800px"
        headerAction={saveButton}
      >
        <EditKGColumnComponent
          id={selectedNode?.id}
          onClose={onClose}

          // For saving the KG Column
          triggerSubmit={triggerSubmit}
          setTriggerSubmit={setTriggerSubmit}
        />
      </CustomModal>
    )}

    {/* KG Column Delete Modals */}
    {openDeleteKgColumnDialog && (
      <AlertDialog 
        isOpen={openDeleteKgColumnDialog}
        leastDestructiveRef={cancelDeleteRef}
        onClose={closeDeleteKgColumnDialog}
      >
        <AlertDialogOverlay
          style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}
        >
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete KG Column
            </AlertDialogHeader>

            <AlertDialogBody>
                Are you sure you want to delete this column?
            </AlertDialogBody>

            <AlertDialogFooter>
                <Button
                ref={cancelDeleteRef}
                colorScheme="green"
                onClick={closeDeleteKgColumnDialog}
                >
                Cancel
                </Button>
                <Button
                colorScheme="red"
                onClick={() => handleDeleteKgColumn(selectedNode?.id!)}
                ml={3}
                >
                Delete
                </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    )}
    {/* KG Column Dependencies Modal */}
    {dependencies.length > 0 && (
      <DependenciesModal
        isOpen={openDeleteKgColumn}
        onClose={closeDeleteKgColumn}
        dependencies={dependencies}
      />
    )}

  </>
  );
};

export default MKGNodeMenuComponent;